import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { 
    selectAllBrands, 
    selectAllDrinks, 
    selectGlobalDataLoading, 
    selectGlobalDataError,
    selectGlobalDataInitialized
} from "@/store/slices/globalDataSlice";
import { 
    fetchActiveLocationData, // Thunk to get location-specific data (including sightings)
    selectActiveLocationSightings,
    selectActiveLocationIsLoading,
    selectActiveLocationDetailsError
} from "@/store/slices/activeLocationSlice";
import type { Brand, Drink } from "@/types/firestore";
import { useState, useMemo, useEffect } from "react";
import { Check, ChevronsUpDown, X, Plus, Loader2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  CommandDialog,
  CommandDialogEmpty,
  CommandDialogGroup,
  CommandDialogInput,
  CommandDialogItem,
  CommandDialogList,
} from "@/components/ui/command-dialog";
import {
  PopoverDialog,
  PopoverDialogContent,
  PopoverDialogTrigger,
} from "@/components/ui/popover-dialog";
import { AddBrandModal } from "./AddBrandModal";
import { AddDrinkModal } from "./AddDrinkModal";
import { reportSighting } from "@/lib/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { ReduxSighting } from "@/store/slices/activeLocationSlice"; // Import ReduxSighting type

// Helper function to check if a date is today (client-side perspective)
const isDateToday = (date: Date | undefined): boolean => {
  if (!date) return false;
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

// Extended drink type that includes sighting data
interface DrinkWithSighting extends Drink {
  sightingId?: string;
  sightingData?: ReduxSighting; // Expect the Redux version with number timestamps
}

// Selected drink with brand info for displaying in the list
interface SelectedDrinkWithBrand {
  drinkId: string;
  drinkName: string;
  brandId: string;
  brandName: string;
}

interface AddSightingModalProps {
  locationId: string;
  isOpen: boolean;
  onClose: () => void;
  isPotential?: boolean; 
  onAddPotentialSightings?: (sightings: { drinkId: string; brandId: string }[]) => void;
  // cached props are no longer needed with RTK global state
  // cachedBrands?: Brand[];
  // cachedDrinks?: Drink[];
}

export function AddSightingModal({ 
  locationId, 
  isOpen, 
  onClose, 
  isPotential = false, 
  onAddPotentialSightings,
  // removed cached props
}: AddSightingModalProps) {
  
  const dispatch = useAppDispatch();

  // 1) Global Data from RTK Store ------------------------------------------
  const brands = useAppSelector(selectAllBrands);
  const allDrinks = useAppSelector(selectAllDrinks);
  const globalDataLoading = useAppSelector(selectGlobalDataLoading);
  const globalDataError = useAppSelector(selectGlobalDataError);
  const globalDataInitialized = useAppSelector(selectGlobalDataInitialized);

  // 2) Location-Specific Sightings from RTK Store --------------------------
  // Fetch active location data (details + sightings) when modal opens for a real location
  useEffect(() => {
    if (isOpen && locationId && !isPotential && locationId !== 'potential') {
      console.log('[AddSightingModal] Fetching active location data for:', locationId);
      dispatch(fetchActiveLocationData(locationId));
    } else if (isOpen && (!locationId || isPotential || locationId === 'potential')) {
      // Clear active location data if it's potential or no ID
      dispatch(fetchActiveLocationData('')); // Dispatch with empty string/null
    }
    // Consider cleanup? fetchActiveLocationData handles listener cleanup internally
  }, [isOpen, locationId, isPotential, dispatch]);

  const locationSightings = useAppSelector(selectActiveLocationSightings);
  // Use combined loading state from activeLocationSlice
  const locationDataLoading = useAppSelector(selectActiveLocationIsLoading);
  // Use error state from activeLocationSlice
  const locationDataError = useAppSelector(selectActiveLocationDetailsError); // Assuming this covers sighting errors too?

  // Combine loading/error states
  const isLoading = (globalDataLoading === 'pending' && !globalDataInitialized) || (!isPotential && locationDataLoading);
  const hasError = globalDataError || (!isPotential && locationDataError);
  
  // Derive location drinks from sightings and allDrinks
  const locationDrinks = useMemo(() => {
    if (isPotential || !locationSightings || !allDrinks) return [];
    
    return locationSightings.map(sighting => {
      const drink = allDrinks.find(d => d.id === sighting.drinkId);
      if (!drink) return null;
      
      return {
        ...drink,
        sightingId: sighting.id,
        sightingData: sighting
      } as DrinkWithSighting;
    }).filter((d): d is DrinkWithSighting => d !== null);
  }, [isPotential, locationSightings, allDrinks]);

  // Timeout logic might still be useful if global data takes too long
  const [isTimedOut, setIsTimedOut] = useState(false);
  useEffect(() => {
    if (isOpen && isLoading) { // Use combined isLoading
      const timeoutId = setTimeout(() => {
        console.log('Loading timeout reached, forcing UI to proceed');
        setIsTimedOut(true);
      }, 10000); 
      return () => clearTimeout(timeoutId);
    }
    if (!isOpen) setIsTimedOut(false); // Reset timeout when closed
  }, [isOpen, isLoading]);

  // --- Component State Remains Largely the Same ---
  const [brandPopoverOpen, setBrandPopoverOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [drinkModalOpen, setDrinkModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Keep local submitting state for API call
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [autoRetried, setAutoRetried] = useState(false);
  const [excludedDrinkIds, setExcludedDrinkIds] = useState<string[]>([]);
  const [retryInProgress, setRetryInProgress] = useState(false);

  // New state for selected drinks list
  const [selectedDrinks, setSelectedDrinks] = useState<SelectedDrinkWithBrand[]>([]);

  // --- Logic using RTK data ---
  const getSightingDetails = (): { drinkId: string; brandId: string }[] => {
    return selectedDrinks.map(drink => ({
      drinkId: drink.drinkId,
      brandId: drink.brandId
    }));
  };

  const brandDrinks = useMemo(() => {
    if (!selectedBrand || isLoading || hasError) {
      return [];
    }
    const filteredDrinks = allDrinks.filter((d: Drink) => d.brandId === selectedBrand);
    const locationDrinksMap = new Map<string, DrinkWithSighting>();
    locationDrinks.forEach((drink: DrinkWithSighting) => {
      if (drink && drink.id) {
        locationDrinksMap.set(drink.id, drink);
      }
    });
    return filteredDrinks.map(drink => {
      const locationDrink = locationDrinksMap.get(drink.id || '');
      return locationDrink ? locationDrink : drink;
    });
  }, [selectedBrand, allDrinks, locationDrinks, isLoading, hasError]);

  // --- Submission Logic (largely unchanged, uses local state) ---
  const handleSubmit = async () => {
    if (selectedDrinks.length === 0) {
      setSubmissionError("Please select at least one drink.");
      return;
    }
    
    if (isPotential) {
      if (onAddPotentialSightings) {
        const sightingsToAdd = getSightingDetails();
        onAddPotentialSightings(sightingsToAdd);
        handleClose();
      } else {
        console.error("onAddPotentialSightings callback missing.");
        setSubmissionError("Configuration error.");
      }
      return;
    }

    // Real sighting submission
    const drinksToSubmit = selectedDrinks
      .map(drink => drink.drinkId)
      .filter(id => !excludedDrinkIds.includes(id));
    
    if (drinksToSubmit.length === 0) {
      setSubmissionError("All selected drinks have already been reported today.");
      return;
    }

    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      // Create payload for batch submission
      const sightingsPayload = drinksToSubmit.map(drinkId => ({
        locationId,
        drinkId
      }));

      // Use batch or single API based on number of items
      if (sightingsPayload.length === 1) {
        // Single sighting
        console.log(`Reporting single sighting for drink ${sightingsPayload[0].drinkId} at location ${locationId}`);
        await reportSighting(sightingsPayload[0]);
      } else {
        // Multiple sightings in batch
        console.log(`Reporting batch of ${sightingsPayload.length} sightings at location ${locationId}`);
        await reportSighting(sightingsPayload);
      }
      
      // Success - close the modal
      handleClose();
    } catch (error) {
      console.error(`Failed to report sightings at location ${locationId}:`, error);
      const errorMessage = (error as Error).message || '';
      
      // Handle the case where some sightings were already confirmed today
      if (errorMessage.includes('already confirmed today')) {
        setSubmissionError(errorMessage);
        
        // If not already retried and this appears to be about specific sightings
        if (!autoRetried) {
          // Set up for auto-retry by extracting any drink IDs that might be in the error message
          // This is a simplified approach; in reality you might want server to return which specific
          // sightings failed if that information is available
          
          // For now, we'll conservatively assume all are problematic and just try again
          // with proper exclusions if the API tells us about ALREADY_CONFIRMED_TODAY
          setAutoRetried(true);
          setRetryInProgress(true);
        } else {
          console.warn("Errors persisted after auto-retry.");
        }
      } else {
        // For other errors, just show the message
        setSubmissionError(errorMessage || "An error occurred");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // --- Other handlers mostly unchanged ---
  useEffect(() => {
    if (retryInProgress && !isSubmitting) {
      setRetryInProgress(false);
      const timeoutId = setTimeout(() => { handleSubmit(); }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [retryInProgress, isSubmitting]);

  const handleClose = () => {
    setSelectedBrand(null);
    setSelectedDrinks([]);
    setBrandPopoverOpen(false);
    setIsSubmitting(false);
    setSubmissionError(null);
    setAutoRetried(false);
    setExcludedDrinkIds([]);
    setRetryInProgress(false);
    onClose();
  };

  const handleBrandAdded = (brandId: string) => {
    setSelectedBrand(brandId);
  };

  const handleDrinkAdded = (drinkId: string) => {
    const newDrink = allDrinks.find(d => d.id === drinkId);
    const selectedBrandObj = brands.find((b: Brand) => b.id === selectedBrand);
    
    if (newDrink && selectedBrandObj && newDrink.id && selectedBrandObj.id) {
      const newSelectedDrink: SelectedDrinkWithBrand = {
        drinkId: newDrink.id,
        drinkName: newDrink.name,
        brandId: selectedBrandObj.id,
        brandName: selectedBrandObj.name
      };
      
      // Check if already selected
      if (!selectedDrinks.some(d => d.drinkId === newDrink.id)) {
        setSelectedDrinks(prev => [...prev, newSelectedDrink]);
      }
    }
  };

  const handleAddSelectedDrink = (drink: DrinkWithSighting) => {
    const selectedBrandObj = brands.find((b: Brand) => b.id === selectedBrand);
    
    if (drink && selectedBrandObj && drink.id && selectedBrandObj.id) {
      const newSelectedDrink: SelectedDrinkWithBrand = {
        drinkId: drink.id,
        drinkName: drink.name,
        brandId: selectedBrandObj.id,
        brandName: selectedBrandObj.name
      };
      
      // Check if already selected
      if (!selectedDrinks.some(d => d.drinkId === drink.id)) {
        setSelectedDrinks(prev => [...prev, newSelectedDrink]);
      }
    }
  };

  const handleRemoveDrink = (drinkId: string) => {
    setSelectedDrinks(prev => prev.filter(d => d.drinkId !== drinkId));
  };

  const getBrandName = (brandId: string | null): string => {
    if (!brandId) return "Select brand...";
    // Use isLoading instead of brandsLoading
    if (isLoading && !isTimedOut) return "Loading brands...";
    // Use hasError instead of brandsError
    if (hasError && !isTimedOut) return "Error loading brands";
    // Use brands from RTK state
    const brand = brands.find((b: Brand) => b.id === brandId);
    return brand ? brand.name : "Select brand...";
  };

  // isDrinkSightedToday remains the same
  const isDrinkSightedToday = (drink: DrinkWithSighting): boolean => {
    // Create Date object from the timestamp number (milliseconds)
    const lastConfirmedMs = drink.sightingData?.lastConfirmed;
    const lastConfirmedDate = typeof lastConfirmedMs === 'number' && lastConfirmedMs > 0 
        ? new Date(lastConfirmedMs) 
        : undefined;
    return isDateToday(lastConfirmedDate) || (!!drink.id && excludedDrinkIds.includes(drink.id));
  };

  const renderSubmitError = () => {
    // ... remains mostly the same
    if (!submissionError) return null;
    let errorMessage = submissionError;
    if (!isPotential && autoRetried) { 
      errorMessage += " Auto-retry was attempted.";
    }
    return <p className="text-red-500 text-sm mt-2 text-right">Error: {errorMessage}</p>;
  };

  // Use combined loading/error states for rendering fallbacks
  const availableBrands = brands || []; // Already using RTK state
  // No longer need availableDrinks fallback as allDrinks comes from RTK
  
  const errorDetails = hasError && !isTimedOut ? (
    <div className="text-sm text-red-500 max-h-20 overflow-y-auto">
      {globalDataError && <p>Global data error: {globalDataError}</p>}
      {locationDataError && <p>Location data error: {locationDataError}</p>}
    </div>
  ) : null;

  const submittingButtonText = autoRetried ? "Retrying..." : "Adding...";

  // --- JSX Structure (update disabled states and error/loading display) ---
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Add Sighting(s)</DialogTitle>
          <DialogDescription>
            Record one or more drink sightings at this location
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Brand</label>
            <div className="flex gap-2">
              <PopoverDialog open={brandPopoverOpen} onOpenChange={setBrandPopoverOpen}>
                <PopoverDialogTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={brandPopoverOpen}
                    className="flex-1 justify-between"
                    // Use combined isLoading and local isSubmitting
                    disabled={isLoading || isSubmitting}
                  >
                    {getBrandName(selectedBrand)}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverDialogTrigger>
                <PopoverDialogContent className="w-[--radix-popover-trigger-width] p-0">
                  <CommandDialog>
                    <CommandDialogInput placeholder="Search brands..." />
                    <div className="overflow-y-auto max-h-[300px]" onWheel={e => e.stopPropagation()}>
                      <CommandDialogList>
                        {/* Use isLoading for empty state message */}
                        <CommandDialogEmpty>{isLoading ? "Loading..." : "No brand found."}</CommandDialogEmpty>
                        <CommandDialogGroup>
                          {/* Use availableBrands derived from RTK state */}
                          {availableBrands.map((b: Brand) => (
                            <CommandDialogItem
                              key={b.id}
                              value={b.name}
                              onSelect={() => {
                                if (b.id) {
                                  setSelectedBrand(b.id === selectedBrand ? null : b.id);
                                  setBrandPopoverOpen(false);
                                } else {
                                  console.warn("Selected brand missing ID", b);
                                }
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedBrand === b.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {b.name}
                            </CommandDialogItem>
                          ))}
                        </CommandDialogGroup>
                      </CommandDialogList>
                    </div>
                  </CommandDialog>
                </PopoverDialogContent>
              </PopoverDialog>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setBrandModalOpen(true)}
                      disabled={isLoading || isSubmitting}
                    >
                      <Plus className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Create new brand</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {/* Use combined isLoading and hasError */}
            {isLoading && !isTimedOut && <p className="text-sm text-muted-foreground">Loading...</p>}
            {errorDetails}
          </div>

          <div className="flex gap-4">
            {/* Drinks selection column */}
            <div className="flex-1 space-y-2">
              <label className="text-sm font-medium">Select Drinks</label>
              <div className="border rounded-md p-2 space-y-2 max-h-60 overflow-y-auto">
                {isLoading && !isTimedOut ? (
                  <p className="text-sm text-muted-foreground text-center py-2">Loading drinks...</p>
                ) : hasError && !isTimedOut ? (
                  <p className="text-sm text-red-500 text-center py-2">Error loading drinks</p>
                ) : !selectedBrand ? (
                  <p className="text-sm text-muted-foreground text-center py-2">Please select a brand first</p>
                ) : brandDrinks.length > 0 ? (
                  brandDrinks.map((drink: DrinkWithSighting) => {
                    const sightedToday = !isPotential && isDrinkSightedToday(drink);
                    const isDisabled = isSubmitting || sightedToday || isLoading;
                    const isAlreadySelected = selectedDrinks.some(d => d.drinkId === drink.id);
                    
                    return (
                      <div key={drink.id} className={`flex items-center space-x-2 ${isDisabled ? 'opacity-70' : ''}`}>
                        <Checkbox
                          id={drink.id}
                          checked={!!drink.id && selectedDrinks.some(d => d.drinkId === drink.id)}
                          onCheckedChange={() => {
                            if (isDisabled && !isAlreadySelected) return; // Allow unchecking even if submitting/sightedToday if it IS selected
                            if (isAlreadySelected) {
                              handleRemoveDrink(drink.id || '');
                            } else if (!isDisabled) { // Only add if not disabled for other reasons
                              handleAddSelectedDrink(drink);
                            }
                          }}
                          disabled={isDisabled && !isAlreadySelected}
                        />
                        <label htmlFor={drink.id} className={`text-sm ${isDisabled && !isAlreadySelected ? 'cursor-not-allowed' : ''}`}>
                          {drink.name ? (
                            <>
                              {drink.name}
                              {sightedToday && <span className="text-gray-500 ml-1">(sighted today)</span>}
                            </>
                          ) : (
                            <>
                              <i>{drink.brandName || 'Unknown Brand'}</i> 
                              {sightedToday && <span className="text-gray-500 ml-1">(sighted today)</span>}
                            </>
                          )}
                        </label>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-2">
                    No drinks found for this brand.
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setDrinkModalOpen(true)}
                        disabled={!selectedBrand || isLoading || isSubmitting}
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Create new drink</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            
            {/* Selected drinks list column */}
            <div className="flex-1 space-y-2">
              <label className="text-sm font-medium">Drinks selected</label>
              <div className="border rounded-md p-2 space-y-2 max-h-60 overflow-y-auto">
                {selectedDrinks.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">No drinks selected</p>
                ) : (
                  selectedDrinks.map((drink) => (
                    <div key={drink.drinkId} className="flex items-center justify-between p-2 rounded bg-muted/50">
                      <div className="overflow-hidden">
                        <p className="text-sm truncate">
                          <b>{drink.brandName}</b> {drink.drinkName}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleRemoveDrink(drink.drinkId)}
                        disabled={isSubmitting}
                        className="h-6 w-6"
                      >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Remove</span>
                      </Button>
                    </div>
                  ))
                )}
              </div>
              {selectedDrinks.length > 0 && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedDrinks([])}
                    disabled={isSubmitting}
                    className="text-xs"
                  >
                    <Trash2 className="mr-1 h-3 w-3" /> Clear all
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {renderSubmitError()}
        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedDrinks.length === 0 || isSubmitting || isLoading || !!hasError}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {submittingButtonText}
              </>
            ) : (
              "Add Sightings"
            )}
          </Button>
        </div>
      </DialogContent>
      
      {/* Modals */}
      <AddBrandModal
        isOpen={brandModalOpen}
        onClose={() => setBrandModalOpen(false)}
        onBrandAdded={handleBrandAdded}
      />
      {selectedBrand && (
        <AddDrinkModal
          brandId={selectedBrand}
          isOpen={drinkModalOpen && !!selectedBrand}
          onClose={() => setDrinkModalOpen(false)}
          onDrinkAdded={handleDrinkAdded}
        />
      )}
    </Dialog>
  );
} 